import React from 'react'

import { Box, useTheme } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

export interface DatePickerFieldProps {
  value: Date | null
  onChange: (value: Date | null) => void
  /** Which view opens first. Design opens on the year grid. */
  openTo?: 'year' | 'month' | 'day'
  disableFuture?: boolean
}

/**
 * App-level static date picker. Consolidates the calendar styling that was
 * duplicated across GeneralInfo + EditFieldModal into one place, matching the
 * profile design (left-aligned header, transparent nav buttons, year grid).
 */
export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  value,
  onChange,
  openTo = 'year',
  disableFuture = true,
}) => {
  const theme = useTheme()

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          '& .MuiPickersArrowSwitcher-button': { backgroundColor: 'transparent' },
          '& .MuiPickersArrowSwitcher-button:hover': { backgroundColor: 'transparent' },
          '& .MuiPickersCalendarHeader-switchViewButton': { backgroundColor: 'transparent' },
          '& .MuiPickersCalendarHeader-switchViewButton:hover': { backgroundColor: 'transparent' },
          '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
          '& .MuiPickersCalendarHeader-root': { p: 0, mb: 1 },
          '& .MuiPickersCalendarHeader-label': { color: theme.palette.text.primary, fontSize: '18px' },
          // Fill the drawer width: spread the day columns edge-to-edge, bigger cells.
          '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': { justifyContent: 'space-between' },
          '& .MuiPickersLayout-root': {
            minWidth: 0,
            width: '100%',
            gridAutoColumns: 'auto',
            overflowX: 'hidden',
          },
          '& .MuiDateCalendar-root': {
            width: '100% !important',
            maxWidth: '100% !important',
            // Pin the height so switching year -> day view doesn't resize the
            // (content-height) drawer, which read as a full-page "flash".
            height: 320,
            maxHeight: 320,
            margin: 0,
          },
          '& .MuiPickersSlideTransition-root': { minHeight: 300 },
          '& .MuiDayCalendar-weekDayLabel, & .MuiPickersDay-root': {
            width: 44,
            height: 44,
            fontSize: '15px',
            margin: 0,
          },
          '& .MuiYearCalendar-root': {
            width: '100% !important',
            maxWidth: '100% !important',
            maxHeight: 260,
          },
          '& .MuiPickersYear-yearButton': { fontSize: '16px', color: theme.palette.text.primary },
          // Hover: light pill with readable dark text (was black-on-black — the
          // year number vanished on hover).
          '& .MuiPickersYear-yearButton:hover': {
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.text.primary,
          },
          '& .MuiPickersYear-yearButton.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
          // Selected: black pill, white text (matches the selected day).
          '& .MuiPickersYear-yearButton.Mui-selected, & .MuiPickersYear-yearButton.Mui-selected:hover, & .MuiPickersYear-yearButton.Mui-selected:focus':
            {
              backgroundColor: theme.palette.common.black,
              color: theme.palette.common.white,
            },
          '& .MuiPickersDay-root.Mui-selected': {
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
            '&:hover': { backgroundColor: theme.palette.common.black },
            '&:focus': { backgroundColor: theme.palette.common.black },
          },
        }}
      >
        <StaticDatePicker
          value={value}
          onChange={v => onChange(v as Date | null)}
          openTo={openTo}
          disableFuture={disableFuture}
          displayStaticWrapperAs='desktop'
          reduceAnimations
        />
      </Box>
    </LocalizationProvider>
  )
}
